function pad2(n: number) {
  return String(n).padStart(2, "0");
}

export function formatYmdHmInTimeZone(date: Date, timeZone: string) {
  const dtf = new Intl.DateTimeFormat("sv-SE", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = dtf.formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value;
  const y = get("year");
  const m = get("month");
  const d = get("day");
  const h = get("hour");
  const min = get("minute");
  if (!y || !m || !d || !h || !min) return { ymd: "", hm: "" };
  return { ymd: `${y}-${m}-${d}`, hm: `${h}:${min}` };
}

export function parseYmdHm(localYmdHm: string) {
  if (typeof localYmdHm !== "string") return null;
  const [d, t] = localYmdHm.split("T");
  if (!d || !t) return null;
  const [y, m, day] = d.split("-").map((v) => Number(v));
  const [hh, mm] = t.slice(0, 5).split(":").map((v) => Number(v));
  if (
    !Number.isFinite(y) ||
    !Number.isFinite(m) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hh) ||
    !Number.isFinite(mm)
  ) {
    return null;
  }
  return { y, m, day, hh, mm, ymd: d, hm: t.slice(0, 5) };
}

/** Wall-clock `YYYY-MM-DDTHH:mm` in IANA timeZone → UTC ISO string. */
export function localDateTimeInTimeZoneToUtcIso(
  localYmdHm: string,
  timeZone: string,
): string | null {
  const parsed = parseYmdHm(localYmdHm);
  if (!parsed) return null;
  const desiredUtcLike = Date.UTC(
    parsed.y,
    parsed.m - 1,
    parsed.day,
    parsed.hh,
    parsed.mm,
    0,
    0,
  );

  let guess = new Date(desiredUtcLike);
  for (let i = 0; i < 4; i++) {
    const got = formatYmdHmInTimeZone(guess, timeZone);
    if (!got.ymd || !got.hm) break;
    const gotParsed = parseYmdHm(`${got.ymd}T${got.hm}`);
    if (!gotParsed) break;
    const gotUtcLike = Date.UTC(
      gotParsed.y,
      gotParsed.m - 1,
      gotParsed.day,
      gotParsed.hh,
      gotParsed.mm,
      0,
      0,
    );
    const diffMs = desiredUtcLike - gotUtcLike;
    if (diffMs === 0) break;
    guess = new Date(guess.getTime() + diffMs);
  }
  return guess.toISOString();
}

export function utcIsoToLocalDateTimeInput(
  iso: string | null | undefined,
  timeZone?: string,
): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";

  if (typeof timeZone === "string" && timeZone.trim()) {
    const { ymd, hm } = formatYmdHmInTimeZone(d, timeZone.trim());
    return ymd && hm ? `${ymd}T${hm}` : "";
  }

  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

/** True when `local` (`YYYY-MM-DDTHH:mm`) is before now in the given IANA timezone. */
export function isLocalDateTimePastInTimeZone(
  local: string,
  timeZone?: string,
): boolean {
  if (!local?.trim()) return false;
  const { dateYmd, timeHm } = splitLocalDateTime(local);
  if (!dateYmd) return false;

  let nowYmd: string;
  let nowHm: string;
  if (typeof timeZone === "string" && timeZone.trim()) {
    const now = formatYmdHmInTimeZone(new Date(), timeZone.trim());
    if (!now.ymd || !now.hm) return false;
    nowYmd = now.ymd;
    nowHm = now.hm;
  } else {
    const now = new Date();
    nowYmd = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
    nowHm = `${pad2(now.getHours())}:${pad2(now.getMinutes())}`;
  }

  if (dateYmd < nowYmd) return true;
  if (dateYmd === nowYmd && timeHm < nowHm) return true;
  return false;
}

export function getNowLocalDateTimeInput(timeZone?: string): string {
  if (typeof timeZone === "string" && timeZone.trim()) {
    try {
      const { ymd, hm } = formatYmdHmInTimeZone(new Date(), timeZone.trim());
      if (ymd && hm) return `${ymd}T${hm}`;
    } catch {
      // fall through
    }
  }
  const now = new Date();
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}T${pad2(now.getHours())}:${pad2(now.getMinutes())}`;
}

export function splitLocalDateTime(value: string) {
  if (!value || typeof value !== "string") {
    return { dateYmd: "", timeHm: "00:00" };
  }
  const [dateYmd, tPart = "00:00"] = value.split("T");
  const timeHm = tPart.length >= 5 ? tPart.slice(0, 5) : "00:00";
  return { dateYmd: dateYmd || "", timeHm };
}

export function joinLocalDateTime(dateYmd: string, timeHm: string) {
  if (!dateYmd) return "";
  const t = timeHm && /^\d{2}:\d{2}$/.test(timeHm) ? timeHm : "00:00";
  return `${dateYmd}T${t}`;
}

export function localDateTimeInputToUtcIso(
  local: string,
  timeZone?: string,
): string {
  if (!local) return "";
  if (typeof timeZone === "string" && timeZone.trim()) {
    const fromTz = localDateTimeInTimeZoneToUtcIso(local, timeZone.trim());
    if (fromTz) return fromTz;
  }
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString();
}
